export type RegistrationIdentityField = 'firstName' | 'lastName' | 'phone' | 'email' | 'businessEmail';

const PLACEHOLDER_NAMES = new Set([
  'test', 'testing', 'tester', 'asdf', 'asdfg', 'asdfgh', 'qwer', 'qwerty', 'zxcv',
  'foo', 'bar', 'baz', 'xxx', 'xyz', 'abc', 'abcd', 'abcdef', 'aaaa', 'bbbb',
  'none', 'unknown', 'n/a', 'na', 'null', 'undefined', 'anonymous', 'anon',
  'firstname', 'lastname', 'fname', 'lname', 'name', 'user', 'username',
  'admin', 'guest', 'fake', 'sample', 'demo', 'temp', 'placeholder',
  'asdfasdf', 'fdsa', 'fdsafdsa',
]);
const KEYBOARD_ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm', 'poiuytrewq', 'lkjhgfdsa', 'mnbvcxz'];
const VOWELS = /[aeiouyàáâäåāèéêëēìíîïīòóôöōùúûüūæøœ]/i;
const LATIN_NAME = /^[\u0041-\u007a\u00c0-\u024f\s.'’-]+$/i;

function uniqueRatio(text: string): number {
  if (!text) return 1;
  return new Set(text.toLowerCase()).size / text.length;
}

function vowelRatio(text: string): number {
  const letters = text.replace(/[^A-Za-z\u00c0-\u024f]/g, '');
  if (!letters) return 1;
  return letters.split('').filter((ch) => VOWELS.test(ch)).length / letters.length;
}

function hasRepeatedBlock(text: string): boolean {
  const compact = text.toLowerCase().replace(/[^a-z]/g, '');
  if (compact.length < 6) return false;
  for (let size = 2; size <= 4; size += 1) {
    const block = compact.slice(0, size);
    if (!block || new Set(block).size === 1) continue;
    const repeats = compact.split(block).length - 1;
    if (repeats >= 3 && block.length * repeats >= compact.length * 0.7) return true;
  }
  return /(.)\1{3,}/i.test(text);
}

function looksLikeKeyboardMash(name: string): boolean {
  const compact = name.toLowerCase().replace(/[^a-z]/g, '');
  if (compact.length < 6) return false;
  if (PLACEHOLDER_NAMES.has(compact)) return true;
  if (KEYBOARD_ROWS.some((row) => compact.includes(row) || (compact.length >= 6 && row.includes(compact)))) return true;
  if (hasRepeatedBlock(compact)) return true;
  if (uniqueRatio(compact) <= 0.34) return true;
  if (vowelRatio(compact) < 0.16) return true;
  return false;
}

export function nameLooksFabricated(value: unknown): boolean {
  const name = String(value || '').trim();
  if (!name) return false;
  if (/\d/.test(name)) return true;
  if (PLACEHOLDER_NAMES.has(name.toLowerCase().replace(/[^a-z]/g, ''))) return true;
  if (!LATIN_NAME.test(name)) return false;
  const letters = name.replace(/[^A-Za-z]/g, '');
  if (letters.length >= 8 && looksLikeKeyboardMash(name)) return true;
  if (letters.length >= 6 && hasRepeatedBlock(name)) return true;
  return false;
}

export function phoneLooksFabricated(value: unknown): boolean {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return false;
  if (digits.length < 7) return true;
  if (new Set(digits).size <= 2) return true;
  if (/^(\d)\1+$/.test(digits)) return true;
  if (/^0?1234567890?$/.test(digits) || /^0?9876543210?$/.test(digits)) return true;
  if (/^(\d{2})\1{3,}$/.test(digits) || /^(\d{3})\1{2,}$/.test(digits)) return true;
  let sequential = 0;
  for (let i = 1; i < digits.length; i += 1) {
    const step = Number(digits[i]) - Number(digits[i - 1]);
    if (step === 1 || step === -1) sequential += 1;
    else sequential = 0;
    if (sequential >= 8) return true;
  }
  return false;
}

export const REJECTED_EMAIL_DOMAIN_MESSAGE =
  'Please use a regular email address. Temporary or disposable email addresses are not allowed.';

export const REGISTRATION_IDENTITY_MESSAGES: Record<RegistrationIdentityField, string> = {
  firstName: 'Please enter a real first name.',
  lastName: 'Please enter a real last name.',
  phone: 'Please enter a real phone number.',
  email: REJECTED_EMAIL_DOMAIN_MESSAGE,
  businessEmail: REJECTED_EMAIL_DOMAIN_MESSAGE,
};

function isIdentityField(value: unknown): value is RegistrationIdentityField {
  return value === 'firstName' || value === 'lastName' || value === 'phone' || value === 'email' || value === 'businessEmail';
}

export function registrationIdentityFieldsFromError(error: unknown): RegistrationIdentityField[] {
  const data = (error as { response?: { data?: { fields?: unknown; message?: unknown } } })?.response?.data;
  const rawFields = data && Array.isArray(data.fields) ? data.fields : [];
  const listed = rawFields.filter(isIdentityField);
  if (listed.length > 0) return Array.from(new Set(listed));

  const message = String(data?.message || '').toLowerCase();
  const inferred: RegistrationIdentityField[] = [];
  if (message.includes('first name')) inferred.push('firstName');
  if (message.includes('last name')) inferred.push('lastName');
  if (message.includes('phone')) inferred.push('phone');
  if (message.includes('disposable') || message.includes('temporary')) inferred.push('email');
  return inferred;
}

export function applyRegistrationIdentityFieldErrors(
  error: unknown,
  apply: (field: RegistrationIdentityField, message: string) => void
): RegistrationIdentityField[] {
  const fields = registrationIdentityFieldsFromError(error);
  fields.forEach((field) => apply(field, REGISTRATION_IDENTITY_MESSAGES[field]));
  return fields;
}

export function focusRegistrationField(elementId: string): void {
  window.requestAnimationFrame(() => {
    const element = document.getElementById(elementId);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (element instanceof HTMLElement) element.focus();
  });
}
